<script setup>
defineOptions({
  name: 'SupportModal',
});

import { ref, computed, getCurrentInstance, onMounted, watch } from 'vue';
import Modal from './Modal.vue';
import FormRichSelect from './Form/FormRichSelect.vue';
import Tooltip from './Tooltip.vue';
import axios from 'axios';

// Access global properties for route function in SSR/CSR
const instance = getCurrentInstance();
const route = instance?.appContext.config.globalProperties.route;

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);

// State
const selectedClient = ref(null);
const clientDetails = ref(null);
const clients = ref([]);
const intermediaries = ref([]);
const loadingClients = ref(false);
const loadingDetails = ref(false);
const loadingIntermediaries = ref(false);

// Computed
const showClientDetails = computed(() => {
  return selectedClient.value && clientDetails.value;
});

// Load initial data when modal opens
const loadInitialData = async () => {
  if (!props.show) return;

  // Load first 20 clients
  await loadClients('');
  // Load intermediaries
  await loadIntermediaries();
};

// Load clients
const loadClients = async (search) => {
  loadingClients.value = true;
  try {
    const response = await axios.get(route('support.clients'), {
      params: { search }
    });
    clients.value = response.data.options ?? [];
  } catch (error) {
    console.error('Error loading clients:', error);
  } finally {
    loadingClients.value = false;
  }
};

// Load client details
const loadClientDetails = async (clientId) => {
  if (!clientId) {
    clientDetails.value = null;
    return;
  }

  loadingDetails.value = true;
  try {
    const response = await axios.get(route('support.clients.details', { client: clientId }));
    clientDetails.value = response.data;
  } catch (error) {
    console.error('Error loading client details:', error);
    clientDetails.value = null;
  } finally {
    loadingDetails.value = false;
  }
};

// Load intermediaries
const loadIntermediaries = async () => {
  loadingIntermediaries.value = true;
  try {
    const response = await axios.get(route('support.intermediaries'));
    intermediaries.value = response.data;
  } catch (error) {
    console.error('Error loading intermediaries:', error);
  } finally {
    loadingIntermediaries.value = false;
  }
};

// Handle client selection change
const handleClientChange = (client) => {
  selectedClient.value = client;
  loadClientDetails(client?.id);
};

// Handle modal close
const handleClose = () => {
  // Reset state
  selectedClient.value = null;
  clientDetails.value = null;
  emit('close');
};

// Load data when modal opens
onMounted(() => {
  loadInitialData();
});

// Watch for show changes
watch(() => props.show, (newShow) => {
  if (newShow) {
    loadInitialData();
  }
});
</script>

<template>
  <Modal
    :show="show"
    title="Ondersteuning"
    size="xl"
    theme="orange"
    :show-default-footer="true"
    :show-cancel-button="true"
    :show-confirm-button="false"
    cancel-button-text="Sluiten"
    @close="handleClose"
    @cancel="handleClose"
  >
    <div class="support-modal-content">
      <section class="support-premium-callout hidden">
        <h3 class="support-premium-callout__title">WIZZR-premium</h3>
        <p class="support-premium-callout__description">
          Wilt u toegang krijgen tot alle functies? Overweeg dan een premium account.
        </p>
        <a
          :href="route('business.trial-request.index')"
          class="support-premium-callout__button"
        >
          Maak vrijblijvend kennis
        </a>
      </section>

      <div class="support-modal-sections">
      <!-- Section: Helpdesk -->
      <div class="support-section">
        <h3 class="section-title">Hulp van uw opdrachtgever</h3>
        <p class="section-description">
          Indien u contracten mist in uw overzicht of wanneer u inhoudelijk vragen heeft met betrekking tot de verantwoording op uw contracten, dan neemt u rechtstreeks contact op met uw opdrachtgever. Wanneer u in de onderstaande lijst op de opdrachtgever klikt, dan worden de contactgegevens getoond.
        </p>

        <!-- Client Selection -->
        <div class="client-selection">
          <label class="form-label">Selecteer opdrachtgever:</label>
          <FormRichSelect
            v-model="selectedClient"
            :search-url="route('support.clients')"
            :initial-options="clients"
            option-label="organisation"
            option-value="id"
            placeholder="Selecteer een opdrachtgever..."
            :return-object="true"
            @selected="handleClientChange"
            class="w-full"
          />
        </div>

        <!-- Client Details -->
        <div v-if="showClientDetails" class="client-details">
          <div class="client-details-grid">
            <!-- Column 1: Adresgegevens -->
            <div class="address-section">
              <h4 class="subsection-title">Adresgegevens</h4>
              <div class="detail-list">
                <div class="detail-item">
                  <strong>Organisatie:</strong> {{ clientDetails.organisation }}
                </div>
                <div class="detail-item">
                  <strong>Adres:</strong> {{ clientDetails.address }}
                </div>
                <div class="detail-item">
                  <strong>Postcode:</strong> {{ clientDetails.postal }}
                </div>
                <div class="detail-item">
                  <strong>Woonplaats:</strong> {{ clientDetails.residence }}
                </div>
              </div>
            </div>

            <!-- Column 2: Contactpersonen -->
            <div class="contacts-section">
              <h4 class="subsection-title">Contactpersonen</h4>
              <div v-if="clientDetails.brokers && clientDetails.brokers.length > 0" class="contacts-list">
                <div
                  v-for="broker in clientDetails.brokers"
                  :key="broker.id"
                  class="contact-item"
                >
                  <Tooltip class="support-contact-tooltip">
                    <button type="button" class="contact-name">
                      {{ `${broker.firstname || ''} ${broker.lastname || ''}`.trim() || 'Onbekende contactpersoon' }}
                    </button>
                    <template #content>
                      <div class="contact-popover">
                        <table class="contact-popover-table">
                          <tbody>
                            <tr>
                              <th>Naam</th>
                              <td>{{ `${broker.firstname || ''} ${broker.lastname || ''}`.trim() || '-' }}</td>
                            </tr>
                            <tr v-if="broker.email">
                              <th>E-mail</th>
                              <td>{{ broker.email }}</td>
                            </tr>
                            <tr v-if="broker.phone">
                              <th>Telefoon</th>
                              <td>{{ broker.phone }}</td>
                            </tr>
                            <tr v-if="broker.mobile">
                              <th>Mobiel</th>
                              <td>{{ broker.mobile }}</td>
                            </tr>
                            <tr v-if="broker.siro">
                              <th>SIRO</th>
                              <td>{{ broker.siro }}</td>
                            </tr>
                            <!-- <tr v-if="broker.status">
                              <th>Status</th>
                              <td>{{ broker.status }}</td>
                            </tr> -->
                          </tbody>
                        </table>
                      </div>
                    </template>
                  </Tooltip>
                </div>
              </div>
              <div v-else class="no-contacts">
                Geen contactpersonen beschikbaar
              </div>
            </div>
          </div>
        </div>

        <div v-if="loadingDetails" class="loading-details">
          <div class="loading-spinner"></div>
          <p class="loading-text">Contactgegevens laden...</p>
        </div>
      </div>

      <!-- Section: Wizzr Intermediairs -->
      <div class="support-section">
        <h3 class="section-title">Wizzr Intermediairs</h3>
        <p class="section-description">
          Wenst u ondersteuning bij de invoer van uw maatschappelijke waarde in WIZZR, bij de werving van medewerkers uit de doelgroep of bijvoorbeeld bij het aanvragen van subsidies met betrekking tot doelgroepers?<br>
          WIZZR brengt u graag in contact met specialisten, die u niet alleen werk uit handen nemen, maar die bovendien zorgen dat u optimaal gebruik kunt maken van alle wet- en regelgeving. Specialisten die bovendien thuis zijn in WIZZR.
        </p>

        <!-- Intermediaries Table -->
        <div v-if="loadingIntermediaries" class="loading-intermediaries">
          <div class="loading-spinner"></div>
          <p class="loading-text">Intermediairs laden...</p>
        </div>

        <div v-else-if="intermediaries.length > 0" class="table-container">
          <table class="intermediaries-table">
            <thead>
              <tr>
                <th>Organisatie</th>
                <th>Adres</th>
                <th>Woonplaats</th>
                <th>Telefoon</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="intermediary in intermediaries"
                :key="intermediary.id"
              >
                <td>{{ intermediary.organisation }}</td>
                <td>{{ intermediary.address }}</td>
                <td>{{ intermediary.residence }}</td>
                <td>{{ intermediary.phone || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="no-intermediaries">
          Geen intermediairs beschikbaar
        </div>
      </div>

      <!-- Section: Hulp van Wizzr -->
      <div class="support-section">
        <h3 class="section-title">Hulp van Wizzr</h3>
        <p class="section-description">
          De meest gestelde vragen staan in de WIZZR Kennisbank. Staat uw vraag daar niet bij dan kunt u uw vraag mailen aan support@wizzr.nl
        </p>

        <div class="action-buttons">
          <a
            href="https://wizzr-kennisbank.groovehq.com/help"
            target="_blank"
            class="wizzr-button wizzr-button-orange"
          >
            KennisBibliotheek
          </a>
          <a
            href="mailto:support@wizzr.nl"
            class="wizzr-button wizzr-button-orange"
          >
            Mail WIZZR Support
          </a>
        </div>
      </div>
      </div>
    </div>
  </Modal>
</template>

